import { Pipe, PipeTransform } from '@angular/core';

/**
 * A custom Angular pipe that transforms the first letter of a string
 * based on the specified transformation mode: either capitalize (uppercase)
 * or lowercase the first character of the string.
 *
 * This pipe is marked as `standalone`, meaning it can be directly imported
 * and used without being part of an Angular module.
 *
 * @remarks
 * The `TransformFirstLetterPipe` can handle empty or invalid string values gracefully,
 * returning an empty string when the input is invalid or empty.
 *
 * @example
 * In an Angular template:
 * ```html
 * <p>{{ 'hello' | transformFirstLetter:'capitalize' }}</p>
 * <p>{{ 'HELLO' | transformFirstLetter:'lowercase' }}</p>
 * ```
 * Output:
 * ```html
 * <p>Hello</p>
 * <p>hELLO</p>
 * ```
 *
 * @example
 * Using the pipe programmatically:
 * ```typescript
 * const pipe = new TransformFirstLetterPipe();
 * console.log(pipe.transform('hello', 'capitalize')); // Output: 'Hello'
 * console.log(pipe.transform('HELLO', 'lowercase'));  // Output: 'hELLO'
 * ```
 *
 * @export
 * @class TransformFirstLetterPipe
 * @implements {PipeTransform}
 */
@Pipe({
  name: 'transformFirstLetter',
  standalone: true,
})
export class TransformFirstLetterPipe implements PipeTransform {
  transform(value: string, mode: 'capitalize' | 'lowercase' = 'capitalize'): string {
    if (!value) return '';

    if (mode === 'lowercase') {
      return value.charAt(0).toLowerCase() + value.slice(1);
    }

    return value.charAt(0).toUpperCase() + value.slice(1);
  }
}
